import type { IAuditLog } from '../../../src/engine/AuditLog';
import { ChangeEngine } from '../../../src/engine/ChangeEngine';
import type { IFileItem } from '../../../src/models/FileItem';
import type { IBatchResult } from '../../../src/models/Operation';
import type { IOrganizationPlan, IPlannedChange } from '../../../src/models/Plan';
import type { IScanResult } from '../../../src/models/ScanResult';
import { resolveChange } from '../../../src/plan/PlanSimulator';
import { GraphService, type IGraphClient } from '../../../src/services/GraphService';
import type { IWorkspace } from '../../api/src/workspace';
import { isPaused } from '../../api/src/workspace';
import type { IQueueItem } from '../../api/src/queue';
import type { IReadResult } from '../api';
import { ensureInbox, extensionOf, getRoot, type IItem, listChildren } from '../graphx';
import { extraById, type IFiledRecord, type IRetentionResult, recordRetention } from '../retention/retention';

export interface IFilingContext {
  graph: IGraphClient;
  workspace: IWorkspace;
  audit: IAuditLog;
  user: string;
}

/** One document to name and file. */
export interface IFileRequest {
  file: IItem;
  /** Folder path from the library root, e.g. 'ABC Corporation/2025/Tax & Compliance'. */
  parentPath: string;
  newName: string;
  reason: string;
  confidence: number;
}

/**
 * Just enough of a scan for the change engine: the library root, the folder the documents are
 * in, and the documents. The engine checks everything against live SharePoint before acting.
 */
export function miniScan(workspace: IWorkspace, rootId: string, folder: { id: string; path: string }, files: IItem[]): IScanResult {
  const now: string = new Date().toISOString();
  const fileMap: { [id: string]: IFileItem } = {};
  for (const f of files) {
    fileMap[f.id] = {
      id: f.id, name: f.name, extension: extensionOf(f.name), size: f.size ?? 0,
      createdDateTime: f.createdDateTime ?? now, lastModifiedDateTime: f.lastModifiedDateTime ?? now,
      createdBy: '', modifiedBy: '', parentId: folder.id, parentPath: folder.path, webUrl: f.webUrl ?? '', mimeType: ''
    };
  }
  const folderItem = (id: string, name: string, path: string, parentId?: string) => ({
    id, name, parentId, path, depth: path ? path.split('/').length : 0, lastModifiedDateTime: now, webUrl: '',
    childFolderIds: [], fileIds: [], totalFileCount: 0, totalSize: 0
  });
  const folders = { [rootId]: folderItem(rootId, workspace.libraryName, '') };
  if (folder.id !== rootId) {
    folders[folder.id] = folderItem(folder.id, folder.path.split('/').pop() ?? '', folder.path, rootId);
  }
  return {
    library: { id: workspace.driveId, name: workspace.libraryName, webUrl: workspace.webUrl },
    rootId, folders, files: fileMap, fileCount: files.length, folderCount: Object.keys(folders).length - 1,
    totalSize: 0, maxDepth: 1, managedItemCount: 0, protectedFolders: {}, scannedAt: now, durationMs: 0
  };
}

export function fileDocumentChange(r: IFileRequest): IPlannedChange {
  return {
    id: `fileDocument:${r.file.id}`, type: 'fileDocument', itemKind: 'file', targetId: r.file.id,
    suggestedParentPath: r.parentPath, suggestedName: r.newName, needsInput: false,
    reason: r.reason, confidence: r.confidence, issueIds: []
  };
}

/**
 * Names and files documents that are all in one folder (the Inbox): a dry run first, then only
 * the changes that passed it. Every step is written to the audit log and can be undone from History.
 */
export async function fileDocuments(ctx: IFilingContext, folder: { id: string; path: string }, requests: IFileRequest[]): Promise<{ batch?: IBatchResult; problems: { [itemId: string]: string } }> {
  const problems: { [itemId: string]: string } = {};
  if (requests.length === 0) {
    return { problems };
  }
  const root: IItem = await getRoot(ctx.graph, ctx.workspace.driveId);
  const scan: IScanResult = miniScan(ctx.workspace, root.id, folder, requests.map(r => r.file));
  const plan: IOrganizationPlan = { changes: requests.map(fileDocumentChange), quarantinePath: `Set aside by AI Admin/${new Date().toISOString().slice(0, 10)}` };
  const engine: ChangeEngine = new ChangeEngine({
    scan, plan, writer: new GraphService(ctx.graph).createDriveWriter(ctx.workspace.driveId), audit: ctx.audit, user: ctx.user
  });
  const resolved = plan.changes.map(c => resolveChange(scan, c, { status: 'approved' }));
  const checks = await engine.dryRun(resolved);
  const ready = resolved.filter((r, i) => {
    if (!checks[i].ready) {
      problems[r.change.targetId] = checks[i].problems.join(' ');
    }
    return checks[i].ready;
  });
  if (ready.length === 0) {
    return { problems };
  }
  const batch: IBatchResult = await engine.apply(ready);
  for (const e of batch.entries) {
    if (e.status === 'failed' && e.changeId) {
      problems[e.itemId] = e.errorMessage ?? 'Could not be filed.';
    }
  }
  return { batch, problems };
}

/** The files and path of a filing request, from what was read. */
export function requestFromRead(file: IItem, r: IReadResult): IFileRequest | undefined {
  const { rendered, confidence } = r.decision;
  if (!rendered.name || !rendered.folders) {
    return undefined;
  }
  return {
    file, parentPath: rendered.folders.join('/'), newName: rendered.name,
    reason: 'Named and filed automatically from what the document says.',
    confidence: Math.min(confidence.date, confidence.client, confidence.docType)
  };
}

/** The free trial's automatic filing allowance ran out; `left` documents could still be filed. */
export class TrialEndedError extends Error {
  constructor(message: string, public readonly left: number) {
    super(message);
  }
}

/**
 * What the Inbox check needs besides SharePoint: reading documents with the AI (metered) and the
 * review queue. The browser calls our API for these; the background filer calls them directly.
 */
export interface IInboxDeps {
  read(files: { id: string; name: string; extension: string; folderPath: string; downloadUrl?: string }[]): Promise<{ results: IReadResult[]; balance?: number }>;
  getQueue(): Promise<IQueueItem[]>;
  putQueue(items: IQueueItem[]): Promise<void>;
  /** Takes documents out of the queue (e.g. a scan that couldn't be read before and is now filed). */
  removeFromQueue?(itemIds: string[]): Promise<void>;
  /**
   * Called with the whole queue after new documents were put in it, e.g. to mirror it to the
   * Filing Review list in SharePoint. Best effort: a problem here is logged and never stops filing.
   */
  afterQueueChange?: (items: IQueueItem[]) => Promise<void>;
}

/** Tells `afterQueueChange` about the queue as it is now; a problem is only logged. */
export async function afterQueueChange(deps: IInboxDeps): Promise<void> {
  if (!deps.afterQueueChange) {
    return;
  }
  try {
    await deps.afterQueueChange(await deps.getQueue());
  } catch (e) {
    console.warn('Could not update the Filing Review list', e instanceof Error ? e.message : e);
  }
}

export interface IInboxProgress {
  total: number;
  read: number;
}

export interface IInboxResult {
  filed: number;
  queued: number;
  unreadable: number;
  failed: { name: string; message: string }[];
  batch?: IBatchResult;
  /** Documents left this month, from the backend. */
  balance?: number;
  /** The free trial's automatic filing is used up; the rest stay in Inbox untouched. */
  trialEnded?: string;
  /** Regulated packs: how long each filed record is kept, written on the documents. */
  retention?: IRetentionResult;
}

const BATCH: number = 5;

/** Tries at reading a scan before a person is asked. */
const MAX_READ_ATTEMPTS: number = 3;

/** The text reader failed for a passing reason (busy, timeout, its own error), not the document. */
export function couldNotRead(status: string | undefined): boolean {
  return /^ocr-(http|timeout|failed)/.test(status ?? '');
}

/**
 * Checks the Inbox once: reads every new document (ones already in the review queue are
 * skipped, so nothing is read or charged twice), files the ones it is sure about unless filing
 * is paused, and puts the rest in the review queue with the reasons.
 */
export async function checkInbox(ctx: IFilingContext, deps: IInboxDeps, onProgress?: (p: IInboxProgress) => void): Promise<IInboxResult> {
  const w: IWorkspace = ctx.workspace;
  const inbox: IItem = await ensureInbox(ctx.graph, w.driveId, w.inboxName, w.inboxFolderId);
  const known: { [id: string]: boolean } = {};
  // Documents that couldn't be read yet (e.g. OCR was busy) are read again, counting the tries.
  const retries: { [id: string]: number } = {};
  for (const q of await deps.getQueue()) {
    if (q.status === 'retry') {
      retries[q.itemId] = q.attempts ?? 1;
    } else {
      known[q.itemId] = true;
    }
  }
  const files: IItem[] = (await listChildren(ctx.graph, w.driveId, inbox.id)).filter(i => !i.isFolder && !known[i.id]);
  const result: IInboxResult = { filed: 0, queued: 0, unreadable: 0, failed: [] };
  const toFile: IFileRequest[] = [];
  const toQueue: IQueueItem[] = [];
  const reads: { [id: string]: IReadResult } = {};
  const paused: boolean = isPaused(w);
  onProgress?.({ total: files.length, read: 0 });

  for (let i = 0; i < files.length; i += BATCH) {
    let slice: IItem[] = files.slice(i, i + BATCH);
    const send = (s: IItem[]) => deps.read(s.map(f => ({
      id: f.id, name: f.name, extension: extensionOf(f.name), folderPath: inbox.name, downloadUrl: f.downloadUrl
    })));
    let read: Awaited<ReturnType<IInboxDeps['read']>>;
    try {
      read = await send(slice);
    } catch (e) {
      // Free trial: file what's left of it, then leave the rest in Inbox untouched.
      if (e instanceof TrialEndedError) {
        const left: number = e.left;
        if (left > 0 && left < slice.length) {
          slice = slice.slice(0, left);
          read = await send(slice);
          result.trialEnded = e.message;
        } else {
          result.trialEnded = e.message;
          break;
        }
      } else {
        throw e;
      }
    }
    const { results, balance } = read;
    result.balance = balance;
    for (const r of results) {
      reads[r.id] = r;
      const file: IItem = slice.filter(f => f.id === r.id)[0];
      const request: IFileRequest | undefined = file ? requestFromRead(file, r) : undefined;
      // A scan Microsoft's text reader couldn't read just now (busy, timed out) wasn't really read:
      // it stays in Inbox and is tried again at the next check; after 3 tries a person is asked.
      if (file && couldNotRead(r.excerptStatus)) {
        const attempts: number = (retries[file.id] ?? 0) + 1;
        if (attempts < MAX_READ_ATTEMPTS) {
          toQueue.push({
            itemId: file.id, fileName: file.name, extension: extensionOf(file.name), readAt: new Date().toISOString(),
            extraction: {}, status: 'retry', attempts, readStatus: r.excerptStatus,
            reasons: ['This scan couldn\'t be read yet (Microsoft\'s text reader was busy). I\'ll try again at the next check.']
          });
        } else {
          result.unreadable++;
          toQueue.push({
            itemId: file.id, fileName: file.name, extension: extensionOf(file.name), readAt: new Date().toISOString(),
            extraction: {}, status: 'waiting', readStatus: r.excerptStatus,
            reasons: [`This scan couldn't be read after ${MAX_READ_ATTEMPTS} tries. Check that it opens, then fill in what you can.`]
          });
        }
        continue;
      }
      if (file && request && r.decision.action === 'file' && !paused) {
        toFile.push(request);
      } else if (file) {
        const unreadable: boolean = !r.extraction.docType && !r.extraction.client && !r.extraction.folder && !r.extraction.title;
        if (unreadable) {
          result.unreadable++;
        }
        toQueue.push({
          itemId: file.id, fileName: file.name, extension: extensionOf(file.name), readAt: new Date().toISOString(),
          extraction: r.extraction, status: 'waiting', readStatus: r.excerptStatus,
          reasons: paused && r.decision.action === 'file'
            ? ['Automatic filing is paused, so this waits for you. Everything was clear.']
            : unreadable ? ['Nothing readable was found in this document. Please fill in what you can.'] : r.decision.reasons
        });
      }
    }
    onProgress?.({ total: files.length, read: Math.min(files.length, i + BATCH) });
    if (result.trialEnded) {
      break;
    }
  }

  const filed: IFiledRecord[] = [];
  const cleared: string[] = [];
  if (toFile.length) {
    const { batch, problems } = await fileDocuments(ctx, { id: inbox.id, path: inbox.name }, toFile);
    result.batch = batch;
    for (const req of toFile) {
      const problem: string | undefined = problems[req.file.id];
      if (problem) {
        // Keep it in view with what was read: a person decides what to do.
        result.failed.push({ name: req.file.name, message: problem });
        toQueue.push({
          itemId: req.file.id, fileName: req.file.name, extension: extensionOf(req.file.name), readAt: new Date().toISOString(),
          extraction: reads[req.file.id]?.extraction ?? {}, status: 'waiting', reasons: [`It couldn't be filed: ${problem}`]
        });
      } else {
        result.filed++;
        if (retries[req.file.id] !== undefined) {
          cleared.push(req.file.id);
        }
        const d = reads[req.file.id]?.decision;
        filed.push({ itemId: req.file.id, docType: d?.fields.docType, date: d?.fields.date, extra: extraById(d?.extra) });
      }
    }
    // Law (and other regulated packs): record category and keep-until date on each filed document.
    result.retention = await recordRetention(ctx.graph, w, filed);
  }
  if (cleared.length && deps.removeFromQueue) {
    await deps.removeFromQueue(cleared).catch(() => undefined);
  }
  if (toQueue.length) {
    await deps.putQueue(toQueue);
    await afterQueueChange(deps);
  }
  // Scans being tried again aren't waiting for anyone yet.
  result.queued = toQueue.filter(q => q.status === 'waiting').length;
  return result;
}
